import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Printer, ArrowLeft, Sprout, MessageCircle, Sparkles } from "lucide-react";

/**
 * Generic key-stage book viewer used by KS1 / KS3 / KS4 starter scaffolds.
 * Driven by a `meta` and `chapters` data object, identical to the
 * `foundation_book` shape but extended with optional inquiry fields used by
 * the secondary-stage levels (vocab, source quote, written task).
 */
export default function StageBookViewer({
  meta,
  chapters,
  accent = "#CFA052",
  testIdPrefix,
  comingSoonNote,
}) {
  const [active, setActive] = useState(chapters[0]?.n);
  const current = chapters.find((c) => c.n === active) || chapters[0];

  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [active]);

  return (
    <div className="bg-pearl min-h-screen" data-testid={`${testIdPrefix}-book`}>
      <style>{`@media print { .no-print { display: none !important; } .print-section { page-break-after: always; } body { background: white; } }`}</style>

      <header className="text-pearl py-12 px-4 sm:px-8 no-print" style={{ background: `linear-gradient(135deg, #0B1F4D 0%, ${accent} 100%)` }}>
        <div className="max-w-6xl mx-auto">
          <Link to="/education" className="inline-flex items-center gap-2 text-saffron text-xs uppercase tracking-widest hover:text-saffron-light mb-5">
            <ArrowLeft size={14} /> Back to Education
          </Link>
          <p className="text-[10px] uppercase tracking-[0.3em] text-saffron">{meta.series}</p>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">{meta.title}</h1>
          <p className="text-pearl/75 mt-2 text-lg">{meta.subtitle}</p>
          <p className="mt-3 inline-flex items-center gap-2 text-xs text-saffron/85 bg-saffron/10 border border-saffron/30 px-3 py-1.5 rounded-full">
            <Sprout size={12} /> {meta.status}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={() => window.print()}
              data-testid={`${testIdPrefix}-print`}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-saffron text-navy rounded-md text-sm font-medium hover:bg-saffron/90"
            >
              <Printer size={16} /> Print this chapter
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8">
        <aside className="no-print">
          <div className="lg:sticky lg:top-24">
            <div className="text-[10px] uppercase tracking-widest text-saffron mb-3">Chapters</div>
            <ul className="space-y-1.5" data-testid={`${testIdPrefix}-index`}>
              {chapters.map((c) => (
                <li key={c.n}>
                  <button
                    onClick={() => setActive(c.n)}
                    data-testid={`${testIdPrefix}-link-${c.n}`}
                    className={`w-full text-left text-sm px-3 py-2 rounded-md transition-colors ${
                      active === c.n ? "bg-navy text-pearl" : "text-navy/70 hover:bg-saffron/10 hover:text-saffron"
                    }`}
                  >
                    <span className="font-display text-saffron mr-2">{c.n}</span>
                    {c.title}
                  </button>
                </li>
              ))}
            </ul>
            {comingSoonNote && (
              <div className="mt-6 px-3 py-3 bg-saffron/10 border border-saffron/30 rounded-md text-xs text-navy/70 leading-relaxed">
                <strong>More coming.</strong> {comingSoonNote}
              </div>
            )}
          </div>
        </aside>

        <article className="bg-white rounded-lg border border-navy/10 shadow-sm p-6 sm:p-10 print-section" data-testid={`${testIdPrefix}-chapter-${current.n}`}>
          <div className="border-b border-navy/10 pb-5 mb-6">
            <p className="text-[10px] uppercase tracking-widest text-saffron">Chapter {current.n}</p>
            <h2 className="font-display text-4xl text-navy mt-1">{current.title}</h2>
            <p className="mt-3 text-navy/65 italic">{current.bigIdea}</p>
          </div>

          {current.vocab && (
            <div className="mb-6 bg-navy/5 border border-navy/10 rounded-md p-4">
              <h4 className="font-display text-sm text-navy mb-2 uppercase tracking-widest">Key words</h4>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                {current.vocab.map(([term, def]) => (
                  <div key={term}>
                    <dt className="font-semibold text-navy">{term}</dt>
                    <dd className="text-navy/70">{def}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          <h3 className="font-display text-2xl text-navy">{current.storyTitle}</h3>
          <div className="mt-3 space-y-3 text-lg leading-relaxed text-navy/85">
            {current.story.map((p, i) => (
              <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
            ))}
          </div>

          {current.source && (
            <blockquote className="mt-7 border-l-4 border-saffron pl-5 py-2 italic text-navy/85 bg-saffron/5">
              <span dangerouslySetInnerHTML={{ __html: current.source.quote }} />
              <footer className="not-italic mt-2 text-xs text-navy/55">— {current.source.attribution}</footer>
            </blockquote>
          )}

          <div className="mt-10 bg-saffron/5 border border-saffron/30 rounded-md p-5">
            <h3 className="font-display text-lg text-navy inline-flex items-center gap-2">
              <Sparkles size={16} className="text-saffron" /> {current.activityTitle}
            </h3>
            <ol className="mt-3 list-decimal pl-5 space-y-1.5 text-navy/85">
              {current.activity.map((a, i) => (
                <li key={i} dangerouslySetInnerHTML={{ __html: a }} />
              ))}
            </ol>
          </div>

          <div className="mt-7">
            <h3 className="font-display text-lg text-navy inline-flex items-center gap-2">
              <MessageCircle size={16} className="text-saffron" /> {current.discussionTitle || "Talk together"}
            </h3>
            <ul className="mt-3 space-y-2 text-navy/85">
              {current.talkPrompts.map((p, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-saffron">·</span>
                  <span dangerouslySetInnerHTML={{ __html: p }} />
                </li>
              ))}
            </ul>
          </div>

          {current.writtenTask && (
            <div className="mt-7 border border-navy/15 rounded-md p-4 bg-pearl/40">
              <h4 className="font-display text-sm uppercase tracking-widest text-navy mb-2">Written task</h4>
              <p className="text-sm text-navy/80" dangerouslySetInnerHTML={{ __html: current.writtenTask }} />
            </div>
          )}

          <div className="mt-10 pt-6 border-t border-navy/10 flex justify-between text-xs text-navy/50">
            <span>{meta.title}</span>
            <span>{meta.edition}</span>
          </div>
        </article>
      </div>
    </div>
  );
}
